import React, {useRef} from 'react';
import {Button, Col, Form, Row, Typography, Flex, Input, InputRef} from 'antd';
import {auth} from '@core/modules/authentication/infra/container.registry';
import {Notification} from '@presentation/components/common';
import {validateMessages} from '../../helpers/message';

import style from '../style';

interface IFormVerifyCode {
    loading: boolean;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setIsVerifyCode: React.Dispatch<React.SetStateAction<boolean>>;
    email: string;
    setCode: React.Dispatch<React.SetStateAction<string>>;
}

interface TFormVerifyCode {
    codeOne: string;
    codeTwo: string;
    codeThree: string;
    codeFour: string;
    codeFive: string;
    codeSix: string;
}

const CODE_FIELDS: (keyof TFormVerifyCode)[] = [
    'codeOne',
    'codeTwo',
    'codeThree',
    'codeFour',
    'codeFive',
    'codeSix',
];

const digitInputStyle: React.CSSProperties = {
    width: 44,
    height: 44,
    borderRadius: 8,
    textAlign: 'center',
    fontSize: 20,
    fontWeight: 600,
    padding: 0,
};

const FormVerifyCode = (props: IFormVerifyCode) => {
    const [form] = Form.useForm<TFormVerifyCode>();
    const inputRefs = useRef<(InputRef | null)[]>([]);

    const handleVerify = async (values: TFormVerifyCode) => {
        try {
            props.setLoading(true);
            const code: string = CODE_FIELDS.map((f) => values[f] ?? '').join('');

            const isSendCode = await auth.forgotPassword.validateCode({
                code,
                email: props.email,
            });
            props.setCode(code);
            props.setIsVerifyCode(isSendCode);
        } catch (error: any) {
            Notification.error({
                message: error.message,
            });
        } finally {
            props.setLoading(false);
        }
    };

    const handleKeyUp = (
        e: React.KeyboardEvent<HTMLInputElement>,
        index: number,
        fieldName: keyof TFormVerifyCode
    ) => {
        const key = e.key;

        // Backspace: limpa e volta ao campo anterior
        if (key === 'Backspace') {
            form.setFieldValue(fieldName, '');
            if (index > 0) {
                inputRefs.current[index - 1]?.focus();
            }
            return;
        }

        // Aceita apenas dígitos
        if (!/^\d$/.test(key)) return;

        // Preenche o campo atual e avança para o próximo
        form.setFieldValue(fieldName, key);
        if (index < CODE_FIELDS.length - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    return (
        <div style={style.container}>
            <Typography.Title style={style.description}>
                Insira os 6 dígitos que enviamos para você para recuperar a senha
            </Typography.Title>

            <Form
                form={form}
                name="forgot_password-verify-code"
                layout="vertical"
                validateMessages={validateMessages}
                onFinish={handleVerify}
                autoComplete="off"
                style={style.form}
            >
                <Flex justify="center" gap={8} style={{marginBottom: 24}}>
                    {CODE_FIELDS.map((fieldName, index) => (
                        <Form.Item
                            key={fieldName}
                            name={fieldName}
                            noStyle
                            rules={[{required: true, message: ''}]}
                        >
                            <Input
                                ref={(el) => {
                                    inputRefs.current[index] = el;
                                }}
                                inputMode="numeric"
                                maxLength={1}
                                style={digitInputStyle}
                                onKeyUp={(e) => handleKeyUp(e, index, fieldName)}
                                onFocus={(e) => e.target.select()}
                                onPaste={(e) => e.preventDefault()}
                            />
                        </Form.Item>
                    ))}
                </Flex>

                <Row justify="end">
                    <Col>
                        <Button
                            type="primary"
                            htmlType="submit"
                            className="login-form-button"
                            size={'middle'}
                            loading={props.loading}
                        >
                            VERIFICAR
                        </Button>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export {FormVerifyCode};
